<template>
  <v-form ref="form" @input="onChange">
    <v-row>
      <v-col cols="12">
        <h3 class="mb-4">Podaci o prodajnom mjestu</h3>
      </v-col>
    </v-row>

    <v-row>
      <v-col cols="12" md="6">
        <div class="d-flex align-center">
          <v-text-field
            autofocus
            dense
            outlined
            label="Naziv prodajnog mjesta"
            v-model="pointOfSale.name"
            :rules="[rules.req]"
            hide-details="auto"
          />
          <field-help-dialog
            title="Naziv prodajnog mjesta"
            description="Jedinstveni naziv za fizičku lokaciju gdje se odvija prodaja - može biti poslovnica, prodavaonica, skladište ili bilo koje drugo mjesto gdje izdajete račune."
            :required="true"
            example="Glavni ured, Poslovnica Zagreb, Prodavaonica Centar, Skladište 1"
            notes="Ovaj naziv pomoći će vam razlikovati različita prodajna mjesta ako ih imate više. Može biti deskriptivan i razumljiv vašim zaposlenicima."
          />
        </div>
      </v-col>
      <v-col cols="12" md="6">
        <div class="d-flex align-center">
          <v-text-field
            dense
            outlined
            label="Oznaka prodajnog mjesta"
            v-model="pointOfSale.location_id"
            :rules="[rules.req, rules.locationMark]"
            hint="Samo brojevi i slova (A-Za-z0-9)"
            persistent-hint
            @keypress="checkInput"
          />
          <field-help-dialog
            title="Oznaka prodajnog mjesta"
            description="Jedinstvena alfanumerička oznaka prodajnog mjesta koja se koristi za identifikaciju u sustavu fiskalizacije. Svaka lokacija mora imati različitu oznaku."
            :required="true"
            format="Samo brojevi i slova bez znakova (A-Za-z0-9)"
            example="PP1, CENTAR, LOK01, SKLADISTE"
            notes="Ova oznaka se koristi u JIR-u (Jedinstveni identifikator računa) pri fiskalizaciji. Nakon što jednom postavite oznaku, ne možete je mijenjati. Oznaka mora biti jedinstvena za sve vaše poslovne prostore."
          />
        </div>
      </v-col>
    </v-row>

    <v-row>
      <v-col cols="12">
        <div class="d-flex align-center">
          <v-text-field
            dense
            outlined
            label="Adresa"
            v-model="pointOfSale.address"
            :rules="[rules.req]"
            hide-details="auto"
          />
          <field-help-dialog
            title="Adresa prodajnog mjesta"
            description="Puna fizička adresa lokacije gdje se odvija prodaja. Ova adresa mora odgovarati stvarnoj lokaciji iz koje se obavlja poslovanje."
            :required="true"
            example="Ilica 123, Trg bana Jelačića 5"
            notes="Automatski se popunjava adresom sjedišta firme, ali možete je promijeniti ako se prodajno mjesto nalazi na drugoj adresi. Ova adresa se može prikazivati na računima."
          />
        </div>
      </v-col>
    </v-row>

    <v-row>
      <v-col cols="12" md="6">
        <div class="d-flex align-center">
          <v-text-field
            dense
            outlined
            label="Grad"
            v-model="pointOfSale.city"
            :rules="[rules.req]"
            hide-details="auto"
          />
          <field-help-dialog
            title="Grad prodajnog mjesta"
            description="Naziv grada ili naselja gdje se nalazi prodajno mjesto."
            :required="true"
            example="Zagreb, Split, Rijeka"
            notes="Automatski se popunjava gradom sjedišta firme, ali ga možete promijeniti ako se prodajno mjesto nalazi u drugom gradu."
          />
        </div>
      </v-col>
      <v-col cols="12" md="6">
        <div class="d-flex align-center">
          <v-text-field
            dense
            outlined
            label="Poštanski broj"
            v-model="pointOfSale.zip_code"
            :rules="[rules.req]"
            hide-details="auto"
          />
          <field-help-dialog
            title="Poštanski broj prodajnog mjesta"
            description="5-znamenkasti poštanski broj koji odgovara adresi prodajnog mjesta."
            :required="true"
            format="5 znamenaka"
            example="10000 (Zagreb), 21000 (Split)"
            notes="Automatski se popunjava poštanskim brojem sjedišta firme. Promijenite ako se prodajno mjesto nalazi na drugoj adresi."
          />
        </div>
      </v-col>
    </v-row>
  </v-form>
</template>

<script>
import rules from '@/plugins/rules'
import FieldHelpDialog from '../shared/FieldHelpDialog.vue'

export default {
  name: 'StepPointOfSale',
  components: {
    FieldHelpDialog
  },
  props: {
    value: {
      type: Object,
      required: true
    },
    existingLocations: {
      type: Array,
      default: () => []
    },
    companyData: {
      type: Object,
      default: null
    }
  },
  data () {
    return {
      pointOfSale: {
        name: '',
        location_id: '',
        address: '',
        city: '',
        zip_code: ''
      },
      dataInitialized: false,
      rules: {
        req: rules.req(),
        locationMark: (val) => {
          const maskRe = /^[A-Za-z0-9]+$/
          if (!val) return true
          if (!maskRe.test(val)) {
            return 'Samo brojevi i slova (A-Za-z0-9)'
          }
          // Provjera duplikata
          const duplicate = this.existingLocations.find(
            loc => loc.location_id === val
          )
          if (duplicate) {
            return 'Oznaka prodajnog mjesta se već koristi'
          }
          return true
        }
      }
    }
  },
  watch: {
    pointOfSale: {
      handler (newVal) {
        this.$emit('input', newVal)
      },
      deep: true
    }
  },
  methods: {
    initializeData () {
      // Inicijaliziraj samo prvi put
      if (this.dataInitialized) {
        return
      }

      // Ako value prop ima podatke, koristi ih
      if (this.value && Object.keys(this.value).length > 0) {
        this.pointOfSale = { ...this.pointOfSale, ...this.value }
        this.dataInitialized = true
      } else if (this.companyData && this.companyData.address) {
        // Ako nema postojećih vrednosti, popuni sa podacima iz firme
        this.pointOfSale.address = this.companyData.address || ''
        this.pointOfSale.city = this.companyData.city || ''
        this.pointOfSale.zip_code = this.companyData.zip_code || ''
        this.dataInitialized = true
      }
    },
    validate () {
      return this.$refs.form.validate()
    },
    onChange () {
      this.$emit('change')
    },
    checkInput (ev) {
      const maskRe = /[A-Za-z0-9]/
      if (!maskRe.test(ev.key)) {
        ev.preventDefault()
        return false
      }
    },
    getData () {
      return this.pointOfSale
    },
    reset () {
      this.dataInitialized = false
      this.pointOfSale = {
        name: '',
        location_id: '',
        address: '',
        city: '',
        zip_code: ''
      }
    }
  },
  mounted () {
    this.initializeData()
  },
  activated () {
    // Ova metoda se poziva svaki put kada v-stepper prikaže ovaj korak
    this.initializeData()
  }
}
</script>

<style scoped>
h3 {
  color: #1577da;
}
</style>
